import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Box,
  Text,
  Flex,
  Icon,
  Badge,
  Alert,
  AlertIcon,
  Spinner,
  HStack,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { CheckCircleIcon, CloseIcon, WarningIcon } from '@chakra-ui/icons';
import { FaFileExcel, FaFileUpload, FaFilter, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import axiosInstance from 'axiosInstance';
import baseUrl from 'Base_Url/baseUrl';
import { useToast } from '@chakra-ui/react';

const SchemeModal = ({

  isOpen,
  onClose,
  searchParams,
  setSearchParams,
  setPage,
  schemes: propsSchemes = [],
  fileTypes: initialFileTypes = [],
  onFileTypesFetched,
}) => {
  const toast = useToast();
  const [modalScheme, setModalScheme] = useState('');
  const [modalFileType, setModalFileType] = useState('');
  const [modalFileTypes, setModalFileTypes] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [loadingSchemes, setLoadingSchemes] = useState(false);
  const [modalTaluka, setModalTaluka] = useState('');
  const [modalFinancialYear, setModalFinancialYear] = useState('');
  const [talukas, setTalukas] = useState([]);
  const [loadingTalukas, setLoadingTalukas] = useState(false);
  // Use refs to prevent unnecessary re-renders
  const hasInitialized = useRef(false);
  const prevPropsSchemes = useRef(propsSchemes);

  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subtitleColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const accentColor = useColorModeValue('purple.500', 'purple.300');
  const disabledBg = useColorModeValue('gray.100', 'gray.700');
  const disabledTextColor = useColorModeValue('gray.400', 'gray.500');
  const disabledBorderColor = useColorModeValue('gray.300', 'gray.600');
  const modalHeaderBg = useColorModeValue(
    'linear(to-r, teal.500, cyan.600)',
    'linear(to-r, teal.600, cyan.700)',
  );
  const modalHeaderTextColor = useColorModeValue('white', 'gray.100');

  const token = localStorage.getItem('token');


  const fetchTalukas = async () => {
    try {
      setLoadingTalukas(true);
      const res = await axiosInstance.get(`${baseUrl}/master/all-tahsil`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res?.data?.data?.data) {
        setTalukas(res?.data?.data?.data);
      }
    } catch (err) {
      console.error("Failed to fetch talukas", err);
    } finally {
      setLoadingTalukas(false);
    }
  };

  // Initialize when modal opens - only once
  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      hasInitialized.current = true;

      const currentScheme = searchParams.get('scheme') || '';
      const currentFileType = searchParams.get('fileType') || '';
      const currentTaluka = searchParams.get('taluka') || '';
      const currentFY = searchParams.get('financialYear') || '';

      setModalScheme(currentScheme);
      setModalFileType(currentFileType);
      setModalTaluka(currentTaluka);
      setModalFinancialYear(currentFY);

      fetchTalukas();

      if (propsSchemes.length > 0) {
        if (currentScheme) {
          fetchFileTypes(currentScheme);
        } else {
          setModalFileTypes([]);
        }
      } else {
        fetchSchemes();
      }
    }

    return () => {
      if (!isOpen) {
        hasInitialized.current = false;
      }
    };
  }, [isOpen]);

  // Handle propsSchemes changes separately
  useEffect(() => {
    if (isOpen && propsSchemes.length > 0 &&
      JSON.stringify(prevPropsSchemes.current) !== JSON.stringify(propsSchemes)) {
      prevPropsSchemes.current = propsSchemes;

      const currentScheme = searchParams.get('scheme') || '';
      if (currentScheme && modalFileTypes.length === 0) {
        fetchFileTypes(currentScheme);
      }
    }
  }, [propsSchemes, isOpen]);

  const fetchSchemes = async () => {
    try {
      setLoadingSchemes(true);
      const schemeRes = await axiosInstance.get(
        `${baseUrl}/master/all-scheme`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (err) {
      if (err.response?.status !== 401) {
        toast({
          title: 'Failed to fetch schemes',
          description: 'Could not load schemes',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setLoadingSchemes(false);
    }
  };

  const fetchFileTypes = async (schemeId) => {
    if (!schemeId) {
      setModalFileTypes([]);
      if (onFileTypesFetched) {
        onFileTypesFetched([]);
      }
      return;
    }

    try {
      setFetching(true);
      const url = `${baseUrl}/master/all-file-type?scheme=${schemeId}`;

      const res = await axiosInstance.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.data?.data) {
        const fetchedFileTypes = res.data.data.data;
        setModalFileTypes(fetchedFileTypes);

        // Update parent component
        if (onFileTypesFetched) {
          onFileTypesFetched(fetchedFileTypes);
        }
      } else {
        setModalFileTypes([]);
        if (onFileTypesFetched) {
          onFileTypesFetched([]);
        }
      }
    } catch (err) {
      if (err.response?.status !== 401) {
        toast({
          title: 'Failed to fetch file types',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
      setModalFileTypes([]);
      if (onFileTypesFetched) {
        onFileTypesFetched([]);
      }
    } finally {
      setFetching(false);
    }
  };

  const handleModalSchemeChange = (e) => {
    const schemeId = e.target.value;
    setModalScheme(schemeId);
    setModalFileType('');

    if (schemeId) {
      fetchFileTypes(schemeId);
    } else {
      setModalFileTypes([]);
      if (onFileTypesFetched) {
        onFileTypesFetched([]);
      }
    }
  };

  const handleModalFileTypeChange = (e) => setModalFileType(e.target.value);
  const handleTalukaChange = (e) => setModalTaluka(e.target.value);
  const handleFYChange = (e) => setModalFinancialYear(e.target.value);

  const applyModalFilters = () => {
    const newParams = new URLSearchParams(searchParams);

    // Existing filters
    if (modalScheme) newParams.set('scheme', modalScheme); else newParams.delete('scheme');
    if (modalFileType) newParams.set('fileType', modalFileType); else newParams.delete('fileType');

    // New filters
    if (modalTaluka) newParams.set('taluka', modalTaluka); else newParams.delete('taluka');
    if (modalFinancialYear) newParams.set('financialYear', modalFinancialYear); else newParams.delete('financialYear');

    setPage(1);
    newParams.set('page', 1);
    setSearchParams(newParams);
    onClose();

    toast({ title: 'Filters applied', status: 'success', duration: 2000, isClosable: true });
  };

  const clearModalFilters = () => {
    setModalScheme('');
    setModalFileType('');
    setModalTaluka('');
    setModalFinancialYear('');
    setModalFileTypes([]);
    if (onFileTypesFetched) onFileTypesFetched([]);
  };

  const getSelectedSchemeName = () => {
    if (!modalScheme) return '';
    const scheme = propsSchemes.find((s) => (s.id || s._id) === modalScheme || String(s.id) === modalScheme || String(s._id) === modalScheme);
    return scheme?.schemeName || scheme?.name || scheme?.title || modalScheme;
  };
  const getSelectedFileTypeName = () => {
    if (!modalFileType) return '';
    const fileType = modalFileTypes.find((t) => (t.id || t._id) === modalFileType);
    return fileType?.name || fileType?.fileType || fileType?.title || 'Selected';
  };
  // FIX: Helper to get Taluka Name safely
  const getSelectedTalukaName = () => {
    if (!modalTaluka) return '';
    const taluka = talukas.find((t) => (t.id || t._id) === modalTaluka);
    return taluka?.name || taluka?.tahsilName || 'Selected';
  };

  // Use propsSchemes directly instead of localSchemes
  const displaySchemes = propsSchemes.length > 0 ? propsSchemes : [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent borderRadius="sm" maxW="40vw" maxH="90vh" bg={cardBg}>
        <ModalHeader
          bgGradient={modalHeaderBg}
          color={modalHeaderTextColor}
          py={3}
          borderTopRadius="sm"
        >
          <Flex align="center" justify="space-between">
            <Box>
              <Text fontSize="xl" fontWeight="bold">
                Select Scheme & File Type
              </Text>
              <Text fontSize="sm" opacity={0.9}>
                Filter files by scheme and file type
              </Text>
            </Box>
            <Badge
              colorScheme="whiteAlpha"
              size="sm"
              px={3}
              py={1}
              borderRadius="full"
              display="flex"
              alignItems="center"
              gap={1}
            >
              <Icon as={FaFilter} boxSize={3} />
              <Text fontSize="xs" fontWeight="bold">
                Filter
              </Text>
            </Badge>
          </Flex>
        </ModalHeader>
        <ModalCloseButton color="white" size="lg" />

        <ModalBody overflow="auto" p={6}>
          <VStack spacing={6} align="stretch">
            {/* SCHEME SELECT
            <Box>
              <Flex align="center" mb={2}>
                <Icon as={FaFileExcel} color="teal.500" mr={2} />
                <Text fontSize="md" fontWeight="semibold" color={textColor}>
                  Select Scheme
                </Text>
                {modalScheme && (
                  <Badge colorScheme="teal" ml={2} size="sm">
                    Selected
                  </Badge>
                )}
                {displaySchemes.length > 0 && (
                  <Badge colorScheme="gray" ml={2} size="sm">
                    {displaySchemes.length} available
                  </Badge>
                )}
              </Flex>

              <Box
                as="select"
                value={modalScheme}
                onChange={handleModalSchemeChange}
                w="100%"
                h="45px"
                px={4}
                borderRadius="lg"
                border="2px solid"
                borderColor={borderColor}
                bg={cardBg}
                color={textColor}
                fontSize="md"
                outline="none"
                cursor="pointer"
                _focus={{
                  borderColor: accentColor,
                  boxShadow: `0 0 0 3px ${accentColor}33`,
                }}
                _hover={{
                  borderColor: accentColor,
                }}
                disabled={loadingSchemes}
              >
                <option value="">-- Select a Scheme --</option>
                {displaySchemes.map((scheme) => (
                  <option
                    key={scheme.id || scheme._id}
                    value={scheme.id || scheme._id}
                  >
                    {scheme.name || scheme.schemeName || scheme.title}
                  </option>
                ))}
              </Box>
            </Box> */}
   {/* FILE TYPE SELECT */}
            <Box>
              <Flex align="center" mb={2}>
                <Icon as={FaFileUpload} color="blue.500" mr={2} />
                <Text fontSize="md" fontWeight="semibold" color={textColor}>
                  Select File Type
                </Text>
                {modalFileType && (
                  <Badge colorScheme="blue" ml={2} size="sm">
                    Selected
                  </Badge>
                )}
                {modalFileTypes.length > 0 && (
                  <Badge colorScheme="gray" ml={2} size="sm">
                    {modalFileTypes.length} available
                  </Badge>
                )}
              </Flex>

              <Box
                as="select"
                value={modalFileType}
                onChange={handleModalFileTypeChange}
                w="100%"
                h="45px"
                px={4}
                borderRadius="lg"
                border="2px solid"
                borderColor={!modalScheme ? disabledBorderColor : borderColor}
                bg={!modalScheme ? disabledBg : cardBg}
                color={!modalScheme ? disabledTextColor : textColor}
                fontSize="md"
                outline="none"
                cursor={!modalScheme ? 'not-allowed' : 'pointer'}
                _focus={{
                  borderColor: modalScheme ? accentColor : disabledBorderColor,
                  boxShadow: modalScheme
                    ? `0 0 0 3px ${accentColor}33`
                    : 'none',
                }}
                _hover={{
                  borderColor: modalScheme ? accentColor : disabledBorderColor,
                }}
                disabled={!modalScheme || fetching}
              >
                <option value="">-- Select a File Type --</option>
                {modalFileTypes.map((type) => (
                  <option key={type.id || type._id} value={type.id || type._id}>
                    {type.name || type.fileType || type.title}
                  </option>
                ))}
              </Box>

              {!modalScheme ? (
                <Text fontSize="xs" color="orange.600" mt={2}>
                  <Icon as={WarningIcon} mr={1} />
                  Please select a scheme first
                </Text>
              ) : fetching ? (
                <Flex align="center" mt={2}>
                  <Spinner size="sm" color="blue.500" mr={2} />
                  <Text fontSize="xs" color={subtitleColor}>
                    Loading file types...
                  </Text>
                </Flex>
              ) : modalFileTypes.length === 0 ? (
                <Text fontSize="xs" color="gray.500" mt={2}>
                  No file types available for this scheme
                </Text>
              ) : (
                <Text fontSize="xs" color="green.600" mt={2}>
                  <Icon as={CheckCircleIcon} mr={1} />
                  {modalFileTypes.length} file type(s) available
                </Text>
              )}
            </Box>
            {/* TALUKA SELECT */}
            {/* <Box>
              <Flex align="center" mb={2}>
                <Icon as={FaMapMarkerAlt} color="purple.500" mr={2} />
                <Text fontSize="md" fontWeight="semibold" color={textColor}>Select Taluka</Text>
              </Flex>
              <Box as="select" value={modalTaluka} onChange={handleTalukaChange} w="100%" h="45px" px={4} borderRadius="lg" border="2px solid" borderColor={borderColor} bg={cardBg} color={textColor}>
                <option value="">-- All Talukas --</option>
                {talukas.map((t) => (
                  <option key={t.id || t._id} value={t.id || t._id}>{t.name || t.tahsilName}</option>
                ))}
              </Box>
            </Box> */}

            

         

            {/* SELECTION SUMMARY BADGES */}
            {(modalScheme || modalFileType || modalTaluka || modalFinancialYear) && (
              <Box
                p={4}
                borderRadius="md"
                bg="gray.50"
                border="1px solid"
                borderColor="gray.200"
              >
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="gray.700"
                  mb={2}
                >
                  Current Selection:
                </Text>
                <SimpleGrid columns={2} spacing={3}>
                  {modalScheme && (
                    <Flex align="center">
                      <Box w="8px" h="8px" borderRadius="full" bg="teal.500" mr={2} />
                      <Text fontSize="xs" color="gray.600">
                        <strong>Scheme:</strong> {getSelectedSchemeName()}
                      </Text>
                    </Flex>
                  )}
                  {modalFileType && (
                    <Flex align="center">
                      <Box w="8px" h="8px" borderRadius="full" bg="blue.500" mr={2} />
                      <Text fontSize="xs" color="gray.600">
                        <strong>Type:</strong> {getSelectedFileTypeName()}
                      </Text>
                    </Flex>
                  )}
                  {modalTaluka && (
                    <Flex align="center">
                      <Box w="8px" h="8px" borderRadius="full" bg="purple.500" mr={2} />
                      <Text fontSize="xs" color="gray.600">
                        <strong>Taluka:</strong> {getSelectedTalukaName()}
                      </Text>
                    </Flex>
                  )}
                  {/* {modalFinancialYear && (
                    <Flex align="center">
                      <Box w="8px" h="8px" borderRadius="full" bg="orange.500" mr={2} />
                      <Text fontSize="xs" color="gray.600">
                        <strong>FY:</strong> {modalFinancialYear}
                      </Text>
                    </Flex>
                  )} */}
                </SimpleGrid>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter
          bg="gray.50"
          borderTop="1px solid"
          borderColor="gray.200"
          py={3}
          px={6}
          gap={3}
          justifyContent="space-between"
          alignItems="center"
        >
          <HStack spacing={3}>
            <Button
              onClick={onClose}
              colorScheme="gray"
              variant="ghost"
              size="md"
              isDisabled={fetching || loadingSchemes}
            >
              Cancel
            </Button>

            <Button
              onClick={clearModalFilters}
              colorScheme="orange"
              variant="outline"
              size="md"
              isDisabled={
                (!modalScheme && !modalFileType && !modalTaluka && !modalFinancialYear) || fetching || loadingSchemes
              }
              leftIcon={<CloseIcon />}
            >
              Clear
            </Button>
          </HStack>

          <HStack spacing={3}>
            <Button
              colorScheme="teal"
              variant="solid"
              size="md"
              onClick={applyModalFilters}
              isLoading={fetching || loadingSchemes}
              loadingText="Applying..."
              isDisabled={!modalScheme}
              leftIcon={<CheckCircleIcon />}
            >
              Apply Filters
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SchemeModal;